import { ChangeEvent } from 'react';

import Dropzone from '@atom/components/common/dropzone/Dropzone';
import { useSchemas } from '@atom/hooks/useSchemas';
import { Icon, IconButton, RadioGroup, Select } from '@atom/mui';
import {
  CategoryIcon,
  CloseIcon,
  FolderIcon,
  GetAppIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { InventoryCategoryTree } from '@atom/types/category';
import { CsvErrorItem } from '@atom/types/error';
import { BULK_EDIT_ASSETS_TEMPLATE_ENDPOINT } from '@atom/utilities/endpoints';
import { buildEndpointWithParamsFromData } from '@atom/utilities/requestUtilities';
import schemaUtilities from '@atom/utilities/schemaUtilities';

import { InventoryUploadType, InventoryUploadView } from './InventoryUpload';
import InventoryUploadError from './InventoryUploadError';

import classes from './inventoryUpload.module.css';

const { RadioButton } = RadioGroup;
const { MenuItem } = Select;

interface Props {
  type: InventoryUploadType;
  onTypeChange: (type: InventoryUploadType) => void;
  file?: File;
  onFileChange: (file?: File) => void;
  schemaId?: string;
  onSchemaChange: (schemaId: string) => void;
  category?: InventoryCategoryTree;
  onCategoryChange: (category?: InventoryCategoryTree) => void;
  onViewChange: (view: InventoryUploadView) => void;
  errors: CsvErrorItem[];
  validating: boolean;
}

const sx = {
  flexDirection: 'row',
};

const inputLabelSx = {
  color: `${colors.neutral.dim} !important`,
  paddingBottom: '0.5rem',
};

const styles = {
  icon: {
    marginRight: '2rem',
    alignSelf: 'center',
  },
  downloadIcon: {
    alignSelf: 'center',
    marginLeft: '1.5rem',
    cursor: 'pointer',
  },
  category: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  link: {
    color: colors.brand.blue,
    cursor: 'pointer',
  },
};

// "Main" view for inventory upload modal content
const InventoryUploadMain = ({
  type,
  onTypeChange,
  file,
  onFileChange,
  schemaId,
  onSchemaChange,
  category,
  onCategoryChange,
  onViewChange,
  errors,
  validating,
}: Props) => {
  const { schemas = [] } = useSchemas();

  const getTemplateDownloadEndpoint = () => {
    return buildEndpointWithParamsFromData(BULK_EDIT_ASSETS_TEMPLATE_ENDPOINT, {
      schemaId,
    });
  };

  return (
    <>
      <RadioGroup
        sx={sx}
        value={type}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onTypeChange(event.target.value as InventoryUploadType)
        }
      >
        <RadioButton
          value={InventoryUploadType.CREATE}
          label={InventoryUploadType.CREATE}
        />
        <RadioButton
          value={InventoryUploadType.UPDATE}
          label={InventoryUploadType.UPDATE}
        />
      </RadioGroup>
      {type === InventoryUploadType.UPDATE && (
        <div className={classes.updateInventoryText}>
          File must ONLY include attributes that you wish to update and values
          that you wish to see in Inventory. Select an inventory type to
          download a template for upload.
        </div>
      )}
      <div className={classes.dropzoneSection}>
        <Dropzone
          onFilesChange={files => onFileChange(files[0] || null)}
          files={file ? [file] : []}
          onFilesRemove={() => onFileChange(null)}
          accept={{ 'text/csv': ['.csv'] }}
          loading={validating}
          loadingText="Validating..."
        />
      </div>
      {type === InventoryUploadType.CREATE && (
        <div className={classes.section}>
          <Icon
            color={
              schemaId
                ? schemaUtilities.getSchemaColorFromColorId(category?.colorId)
                : colors.neutral.gray
            }
            style={styles.icon}
            icon={FolderIcon}
          />
          {category ? (
            <div style={styles.category}>
              <div>{category.name}</div>
              <IconButton onClick={() => onCategoryChange(null)} size="small">
                <Icon icon={CloseIcon} />
              </IconButton>
            </div>
          ) : (
            <div
              style={{ ...styles.category, ...styles.link }}
              onClick={() => onViewChange(InventoryUploadView.INVENTORY_FOLDER)}
            >
              Choose Folder
            </div>
          )}
        </div>
      )}
      <div className={classes.section}>
        <div>
          <Icon style={styles.icon} icon={CategoryIcon} />
        </div>
        <div style={schemaId ? { width: '75%' } : { width: '100%' }}>
          <Select
            label="Inventory Type"
            value={schemaId}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onSchemaChange(event.target.value)
            }
            inputLabelSx={inputLabelSx}
            disabled={!!category?.schemaId}
          >
            {schemas.map(schema => (
              <MenuItem key={schema.id} value={schema.id}>
                {schema.name}
              </MenuItem>
            ))}
          </Select>
        </div>
        {schemaId && (
          <IconButton tooltip="download template">
            <Icon
              onClick={() => window.open(getTemplateDownloadEndpoint())}
              icon={GetAppIcon}
            />
          </IconButton>
        )}
      </div>
      {!!errors.length && (
        <ul className={classes.errorList}>
          {errors.map((err, index) => (
            <InventoryUploadError key={index} error={err} />
          ))}
        </ul>
      )}
    </>
  );
};

export default InventoryUploadMain;
